import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    FaShareAlt,
    FaRegSmile,
    FaBookmark,
    FaUserPlus,
    FaFlag,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBriefcase,
    FaGraduationCap,
    FaHeart,
    FaUsers,
    FaGlobe,
    FaTimes,
    FaArrowLeft
} from "react-icons/fa";
import "./ViewProfile.css";
import image63 from '../img/user.png';
import Like from "../LikeFeature/Like";
import Header from '../Header/Header';
import { trackProfileView } from "../../Components/Utils/analytics";

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  age: number;
  gender: string;
  registeredBy: string;
  country: string;
  state: string;
  city: string;
  occupation: string;
  education: string;
  religion: string;
  maritalStatus: string;
  height: string;
  weight: string;
  annualIncome: string;
  hobbiesAndInterests: string;
  familyDetails: string;
  preferredPartnerAgeRange: string;
  preferredPartnerLocation: string;
  preferredPartnerReligion: string;
  aboutMe: string;
  createdOn: string;
  userId: string;
}

interface ImageData {
    imageUrl: string;
}

interface ReportedProfiles {
    [key: string]: {
        reason: string;
        details: string;
        timestamp: string;
    };
}

const ViewProfile: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [userData, setUserData] = useState<UserData | null>(null);
    const [userProfileImage, setUserProfileImage] = useState<string>("");
    const [userImages, setUserImages] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Feature states
    const [saved, setSaved] = useState<boolean>(false);
    const [followed, setFollowed] = useState<boolean>(false);
    const [showComplimentPopup, setShowComplimentPopup] = useState<boolean>(false);
    const [notification, setNotification] = useState<string>("");
    const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
    const [shareLink, setShareLink] = useState<string>("");
    const [likedProfiles, setLikedProfiles] = useState<{ [key: string]: boolean }>({});
    const [showReportPopup, setShowReportPopup] = useState<boolean>(false);
    const [selectedReportReason, setSelectedReportReason] = useState<string>("");
    const [reportDetails, setReportDetails] = useState<string>("");
    const [isReported, setIsReported] = useState<boolean>(false);
    
    const fromNotification = new URLSearchParams(location.search).get("from") === "notification";

    const compliments = [
        "You're amazing!",
        "Looking great!",
        "I admire your style!",
        "You have a beautiful smile!",
        "Your profile is inspiring!",
        "You seem like a wonderful person!"
    ];
    
    const emojis = ["😊", "❤️", "😎", "🌟", "✨", "🔥"];

    useEffect(() => {
        const fetchUserData = async () => {
            const token = localStorage.getItem('authToken');
            try {
                const response = await fetch(`https://nrimarriage.in/api/v1/users/GetUser?id=${id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                });
                if (!response.ok) {
                    throw new Error(`Error! Status: ${response.status}`);
                }
                const data = await response.json();
                setUserData(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setLoading(false);
            }
        };

        const fetchUserImages = async () => {
            const token = localStorage.getItem('authToken');
            try {
                const response = await fetch(`https://nrimarriage.in/api/v1/users/GetUserImagesWithProfile?userId=${id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    }
                });
                if (!response.ok) {
                    throw new Error(`Error fetching images! Status: ${response.status}`);
                }
                const imagesData = await response.json();
                setUserProfileImage(imagesData.profileImage?.imageUrl || image63);
                setUserImages(imagesData.allImages?.map((img: ImageData) => img.imageUrl) || []);
            } catch (err) {
                console.error('Error fetching images:', err);
                setUserProfileImage(image63);
            }
        };

        if (id) {
            trackProfileView(id);
            fetchUserData();
            fetchUserImages();
        }
    }, [id]);

    // Load report status from localStorage
    useEffect(() => {
        const reportedProfiles: ReportedProfiles = JSON.parse(localStorage.getItem("reportedProfiles") || "{}");
        if (id && reportedProfiles[id]) { 
            setIsReported(true);
            setSelectedReportReason(reportedProfiles[id].reason);
            setReportDetails(reportedProfiles[id].details);
        }
    }, [id]);

    const triggerNotification = (message: string) => {
        setNotification(message);
        setTimeout(() => {
            setNotification("");
        }, 4000);
    };

    const handleLikeChange = (userId: string, newLikeStatus: boolean, isMatch?: boolean) => {
        setLikedProfiles((prev) => ({
            ...prev,
            [userId]: newLikeStatus,
        }));

        if (isMatch) {
            console.log(`Match detected with user ${userId}!`);
        }
    };

    const handleSave = () => {
        setSaved((prev) => !prev);
        triggerNotification(saved ? "Profile removed from saved!" : "Profile saved for later.");
    };

    const handleFollow = () => {
        setFollowed((prev) => !prev);
        triggerNotification(followed ? "Unfollowed the profile!" : "Followed the profile.");
    };

    const handleComplimentSelect = (complimentText: string) => {
        setShowComplimentPopup(false);
        triggerNotification(`Compliment sent: "${complimentText}"`);
    };

    const handleShareClick = () => {
        const profileLink = `${window.location.origin}/profile/${id}`;
        setShareLink(profileLink);
        setShowSharePopup(true);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareLink).then(() => {
            triggerNotification("Link copied to clipboard!");
            setShowSharePopup(false);
        });
    };

    const handleReportSubmit = () => {
        if (!selectedReportReason) {
            triggerNotification("Please select a reason for reporting.");
            return;
        }

        if (id) {
            const reportedProfiles: ReportedProfiles = JSON.parse(localStorage.getItem("reportedProfiles") || "{}");
            reportedProfiles[id] = {
                reason: selectedReportReason,
                details: reportDetails,
                timestamp: new Date().toISOString(),
            };
            localStorage.setItem("reportedProfiles", JSON.stringify(reportedProfiles));

            setIsReported(true);
            triggerNotification("Report submitted successfully. Thank you for your feedback.");
            setShowReportPopup(false);
        }
    };

    const handleBackToProfiles = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <div className="view-profile-page">
                <Header onSearchClick={() => console.log("Search button clicked")} />
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="view-profile-page">
                <Header onSearchClick={() => console.log("Search button clicked")} />
                <div className="error-container">
                    <p className="error-message">Error: {error}</p>
                    <button onClick={handleBackToProfiles} className="action-button-full secondary">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="view-profile-page">
                <Header onSearchClick={() => console.log("Search button clicked")} />
                <div className="loading-container">
                    <p>User profile not found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="view-profile-page">
            <Header onSearchClick={() => console.log("Search button clicked")} />

            {/* Sticky Header with Back Button */}
            <div className="view-profile-header">
                <button
                    className="view-profile-back-button"
                    onClick={handleBackToProfiles}
                    title="Go back"
                >
                    <FaArrowLeft />
                </button>
                <h2 className="view-profile-header-title">Profile</h2>
                <div className="view-profile-header-actions">
                    <button
                        className="share-button-header"
                        onClick={handleShareClick}
                        title="Share profile"
                    >
                        <FaShareAlt />
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <section className="profile-content-section">
                <div className="profile-content-grid">

                    {/* Profile Photo Section */}
                    <div className="profile-photo-section">
                        <img
                            src={userProfileImage}
                            alt={`${userData.firstName}'s profile`}
                            className="profile-main-image"
                        />
                        <div className="profile-name-section">
                            <h1>{userData.firstName}</h1>
                            <p className="profile-age-occupation">
                                {userData.age} years old
                                {userData.occupation && ` • ${userData.occupation}`}
                            </p>
                            {userData.country && (
                                <p className="profile-age-occupation">
                                    <FaMapMarkerAlt style={{ marginRight: '0.5rem' }} />
                                    {userData.country}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Main Content Card */}
                    <div className="profile-main-card">
                        {/* Photo Gallery - Moved to Top */}
                        {userImages.length > 0 && (
                            <div className="profile-section">
                                <h3 className="section-title2">
                                    Photo Gallery
                                </h3>
                                <div className="photo-gallery">
                                    {userImages.map((imageUrl, index) => (
                                        <img 
                                            key={index} 
                                            src={imageUrl} 
                                            alt={`${userData.firstName}'s photo ${index + 1}`} 
                                            className="gallery-image"
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* About Me Section */}
                        {userData.aboutMe && userData.aboutMe !== "N/A" && (
                            <div className="profile-section">
                                <h3 className="section-title2">
                                    <FaHeart />
                                    About Me
                                </h3>
                                <div className="about-me-content">
                                    {userData.aboutMe}
                                </div>
                            </div>
                        )}

                        {/* Personal Details */}
                        <div className="profile-section">
                            <h3 className="section-title2">
                                <FaUsers />
                                Personal Details
                            </h3>
                            <div className="details-grid">
                                <div className="detail-item">
                                    <div className="detail-label">Gender</div>
                                    <div className="detail-value">{userData.gender}</div>
                                </div>
                                <div className="detail-item">
                                    <div className="detail-label">Religion</div>
                                    <div className="detail-value">{userData.religion || "Not specified"}</div>
                                </div>
                                <div className="detail-item">
                                    <div className="detail-label">Marital Status</div>
                                    <div className="detail-value">{userData.maritalStatus || "Not specified"}</div>
                                </div>
                                <div className="detail-item">
                                    <div className="detail-label">Height</div>
                                    <div className="detail-value">{userData.height ? `${userData.height} cm` : "Not specified"}</div>
                                </div>
                            </div>
                        </div>

                        {/* Location & Background */}
                        <div className="profile-section">
                            <h3 className="section-title2">
                                <FaGlobe />
                                Location & Background
                            </h3>
                            <div className="details-grid">
                                <div className="detail-item">
                                    <div className="detail-label">Country</div>
                                    <div className="detail-value">{userData.country}</div>
                                </div>
                                {userData.state && userData.state !== "N/A" && (
                                    <div className="detail-item">
                                        <div className="detail-label">State</div>
                                        <div className="detail-value">{userData.state}</div>
                                    </div>
                                )}
                                {userData.city && userData.city !== "N/A" && (
                                    <div className="detail-item">
                                        <div className="detail-label">City</div>
                                        <div className="detail-value">{userData.city}</div>
                                    </div>
                                )}
                                {userData.education && userData.education !== "N/A" && (
                                    <div className="detail-item">
                                        <div className="detail-label">Education</div>
                                        <div className="detail-value">{userData.education}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Interests & Lifestyle */}
                        {(userData.hobbiesAndInterests && userData.hobbiesAndInterests !== "N/A") && (
                            <div className="profile-section">
                                <h3 className="section-title2">
                                    <FaHeart />
                                    Interests & Hobbies
                                </h3>
                                <div className="about-me-content">
                                    {userData.hobbiesAndInterests}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="profile-sidebar">
                        {/* Quick Stats */}
                        <div className="sidebar-card">
                            <h3 className="section-title2 mb-0">Quick Stats</h3>
                            <div className="quick-stats">
                                <div className="stat-item">
                                    <span className="stat-label">
                                        <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
                                        Age
                                    </span>
                                    <span className="stat-value">{userData.age}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">
                                        <FaMapMarkerAlt style={{ marginRight: '0.5rem' }} />
                                        Location
                                    </span>
                                    <span className="stat-value">{userData.country}</span>
                                </div>
                                {userData.occupation && userData.occupation !== "N/A" && (
                                    <div className="stat-item">
                                        <span className="stat-label">
                                            <FaBriefcase style={{ marginRight: '0.5rem' }} />
                                            Profession
                                        </span>
                                        <span className="stat-value">{userData.occupation}</span>
                                    </div>
                                )}
                                {userData.annualIncome && userData.annualIncome !== "N/A" && (
                                    <div className="stat-item">
                                        <span className="stat-label">💰 Income</span>
                                        <span className="stat-value">{userData.annualIncome}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Partner Preferences */}
                        <div className="sidebar-card">
                            <h3 className="section-title2 mb-0">Partner Preferences</h3>
                            <div className="quick-stats">
                                {userData.preferredPartnerAgeRange && userData.preferredPartnerAgeRange !== "N/A" && (
                                    <div className="stat-item">
                                        <span className="stat-label">Age Range</span>
                                        <span className="stat-value">{userData.preferredPartnerAgeRange}</span>
                                    </div>
                                )}
                                {userData.preferredPartnerLocation && userData.preferredPartnerLocation !== "N/A" && (
                                    <div className="stat-item">
                                        <span className="stat-label">Location</span>
                                        <span className="stat-value">{userData.preferredPartnerLocation}</span>
                                    </div>
                                )}
                                {userData.preferredPartnerReligion && userData.preferredPartnerReligion !== "N/A" && (
                                    <div className="stat-item">
                                        <span className="stat-label">Religion</span>
                                        <span className="stat-value">{userData.preferredPartnerReligion}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Profile Actions */}
                        <div className="actions-section">
                            <h3>Profile Actions</h3>

                            <div className="like-button-container">
                                <div className="like-button-enhanced">
                                    <Like
                                        likedId={userData.userId}
                                        isLiked={likedProfiles[userData.userId]}
                                        onLikeChange={handleLikeChange}
                                        highlightInitially={fromNotification}
                                    />
                                </div>
                            </div>

                            <div className="actions-grid">
                                <button
                                    className={`action-button-enhanced ${saved ? 'saved' : ''}`}
                                    onClick={handleSave}
                                >
                                    <FaBookmark />
                                    {saved ? 'Saved' : 'Save'}
                                </button>

                                <button
                                    className={`action-button-enhanced ${followed ? 'followed' : ''}`}
                                    onClick={handleFollow}
                                >
                                    <FaUserPlus />
                                    {followed ? 'Following' : 'Follow'}
                                </button>

                                <button
                                    className="action-button-enhanced"
                                    onClick={() => setShowComplimentPopup(true)}
                                >
                                    <FaRegSmile />
                                    Compliment
                                </button>

                                <button
                                    className="action-button-enhanced"
                                    onClick={handleShareClick}
                                >
                                    <FaShareAlt />
                                    Share
                                </button>

                                <button
                                    className={`action-button-enhanced ${isReported ? 'reported' : ''}`}
                                    onClick={() => setShowReportPopup(true)}
                                    disabled={isReported}
                                    style={{ gridColumn: '1 / -1' }}
                                >
                                    <FaFlag />
                                    {isReported ? 'Reported' : 'Report Profile'}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Notification Toast */}
            {notification && (
                <div className="notification-toast">
                    {notification}
                </div>
            )}

            {/* Compliment Modal */}
            {showComplimentPopup && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Send a Compliment</h4>
                            <button 
                                className="modal-close"
                                onClick={() => setShowComplimentPopup(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="compliment-options">
                            <div className="compliment-section">
                                <h5>Choose a message</h5>
                                <div className="compliment-grid">
                                    {compliments.map((message, index) => (
                                        <button
                                            key={index}
                                            className="compliment-option"
                                            onClick={() => handleComplimentSelect(message)}
                                        >
                                            {message}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="compliment-section">
                                <h5>Or send an emoji</h5>
                                <div className="emoji-grid">
                                    {emojis.map((emoji, index) => (
                                        <button
                                            key={index}
                                            className="emoji-option"
                                            onClick={() => handleComplimentSelect(emoji)}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {showSharePopup && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Share Profile</h4>
                            <button 
                                className="modal-close"
                                onClick={() => setShowSharePopup(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="share-content">
                            <p>Share this profile with friends and family</p>
                            
                            <div className="share-link-container">
                                <input 
                                    type="text" 
                                    value={shareLink} 
                                    readOnly 
                                    className="share-link-input"
                                />
                                <button className="copy-button" onClick={handleCopyLink}>
                                    Copy
                                </button>
                            </div>
                            
                            <div className="share-buttons">
                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent(`Check out this profile: ${shareLink}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="whatsapp-button"
                                >
                                    📱 Share on WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Report Modal */}
            {showReportPopup && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Report Profile</h4>
                            <button 
                                className="modal-close"
                                onClick={() => setShowReportPopup(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="report-form">
                            <div className="form-group">
                                <label className="form-label">Reason for reporting</label>
                                <select
                                    className="form-select"
                                    value={selectedReportReason}
                                    onChange={(e) => setSelectedReportReason(e.target.value)}
                                >
                                    <option value="">Select a reason</option>
                                    <option value="Sexual Content">Sexual Content</option>
                                    <option value="Fake Profile">Fake Profile</option>
                                    <option value="Harassment or Bullying">Harassment or Bullying</option>
                                    <option value="Spam or Fraud">Spam or Fraud</option>
                                    <option value="Harmful Content">Harmful Content</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label className="form-label">Additional details (optional)</label>
                                <textarea
                                    className="form-textarea"
                                    value={reportDetails}
                                    onChange={(e) => setReportDetails(e.target.value)}
                                    placeholder="Provide more details about the issue..."
                                />
                            </div>
                            
                            <div className="modal-actions">
                                <button 
                                    className="modal-button secondary"
                                    onClick={() => setShowReportPopup(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="modal-button primary"
                                    onClick={handleReportSubmit}
                                >
                                    Submit Report
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewProfile;